import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useMst } from '../../core/stores';
import {
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DigitalOrdersBase, NewOrExisting, OrderStatus } from '../../models';
import { fromUnixTime, format } from 'date-fns';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteIcon from '@material-ui/icons/Delete';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import { PlaylistAddCheck } from '@material-ui/icons';
import { Link } from 'react-router-dom';
import {
  buildFulfilledOrdersCsv,
  downloadCsv,
  fulfilledOrdersCsvFilename,
  OrderExportResolvers,
} from './exportFulfilledOrders';

const useStyles = makeStyles(theme => ({
  button: {
    width: '100%',
  },
  listWidth: {
    width: '100%',
  },
  accordionDetailsLabel: {
    fontWeight: 'bold',
    marginBottom: '0.5rem',
  },
  accordionSummary: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  accordionDetails: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  container: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-start',
  },
  accordionContainer: {
    width: '100%',
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: '2rem',
  },
  actionButton: {
    marginLeft: '2rem',
  },
  exportButton: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
}));

export interface Props {
  orders: DigitalOrdersBase[];
  ordersLoading: boolean;
  loadOrders: () => void;
  readOnly?: boolean;
}

export const OrdersPanel: React.FC<Props> = observer(
  ({ orders, ordersLoading, loadOrders, readOnly = false }) => {
    const { digitalOrdersStore } = useMst();
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [currentOrderId, setCurrentOrderId] = useState('');

    useEffect(() => {
      digitalOrdersStore.loadAllFacilities();
      digitalOrdersStore.loadOrganizations();
    }, [digitalOrdersStore]);

    const handleConfirmBtnClick = () => {
      digitalOrdersStore.removeDigitalOrderById(currentOrderId).then(data => {
        if (data) {
          loadOrders();
        }
        closeModal();
      });
    };

    const closeModal = (): void => {
      setOpen(false);
    };

    const showModal = (orderId): void => {
      setCurrentOrderId(orderId);
      setOpen(true);
    };

    // Export lives on the Fulfilled tab (readOnly) and only on the Vision site — Matrix
    // orders have no Asset Management (touchscreen/LED) counts.
    const showExport = readOnly && import.meta.env.VITE_DO_MODE === 'vision';

    const handleExport = (): void => {
      const resolvers: OrderExportResolvers = {
        getOrgName: id => digitalOrdersStore.organizationMap.get(String(id))?.name,
        getFacilityName: id => digitalOrdersStore.getFacilityById(String(id))?.name,
      };
      const csv = buildFulfilledOrdersCsv(orders, resolvers);
      downloadCsv(fulfilledOrdersCsvFilename(), csv);
    };

    return (
      <>
        {showExport && (
          <Button
            variant="contained"
            color="primary"
            className={classes.exportButton}
            onClick={handleExport}
            disabled={ordersLoading || orders.length === 0}
          >
            Export Fulfilled Digital Order
          </Button>
        )}
        <Grid container direction="row">
          {!ordersLoading &&
            (orders.length ? (
              <div className={classes.listWidth}>
                {orders.map((order, i) => {
                  const newOrg = order.organization.newOrExistingOrganization === NewOrExisting.NEW;
                  const orgName = newOrg
                    ? 'name' in order.organization
                      ? order.organization.name
                      : ''
                    : 'organizationId' in order.organization &&
                      digitalOrdersStore.organizationMap.get(
                        String(order.organization.organizationId),
                      )?.name;
                  const timeLabel =
                    order.orderStatus === OrderStatus.FULFILLED
                      ? `Fulfilled: ${
                          order.fulfillmentTime
                            ? format(fromUnixTime(order.fulfillmentTime), 'MM-dd-yyyy')
                            : 'N/A'
                        }`
                      : order.orderStatus === OrderStatus.SUBMITTED
                      ? `Submitted: ${format(fromUnixTime(order.updated), 'MM-dd-yyyy')}`
                      : `Created: ${format(fromUnixTime(order.created), 'MM-dd-yyyy')}`;
                  return (
                    <div className={classes.container}>
                      <Accordion key={order.id + i} className={classes.accordionContainer}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <div className={classes.accordionSummary}>
                            <Typography>{orgName}</Typography>
                            <Typography variant="subtitle2">{timeLabel}</Typography>
                          </div>
                        </AccordionSummary>
                        <AccordionDetails>
                          <div className={classes.accordionDetails}>
                            <Typography
                              variant="subtitle2"
                              className={classes.accordionDetailsLabel}
                            >
                              Facilities
                            </Typography>
                            {order.facility.facilities.length
                              ? order.facility.facilities.map((fac, i) => {
                                  return (
                                    <Typography variant="subtitle2" key={fac.facilityId + i}>
                                      {fac.name ||
                                        digitalOrdersStore.getFacilityById(String(fac.facilityId))
                                          ?.name}
                                    </Typography>
                                  );
                                })
                              : 'No facility selected'}
                            <div className={classes.actionButtons}>
                              {readOnly ? (
                                <Link to={`/manage-orders/${order.id}`}>
                                  <Button variant="contained" color="primary">
                                    View
                                  </Button>
                                </Link>
                              ) : (
                                <>
                                  <Link to={`/manage-orders/${order.id}`}>
                                    <Tooltip title="Review Order" className={classes.actionButton}>
                                      <IconButton edge="end" aria-label="review">
                                        <PlaylistAddCheck />
                                      </IconButton>
                                    </Tooltip>
                                  </Link>
                                  <Tooltip title="Delete Order" className={classes.actionButton}>
                                    <IconButton
                                      edge="end"
                                      aria-label="delete"
                                      onClick={() => showModal(order.id)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </div>
                          </div>
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Typography variant="h6">
                {readOnly ? 'No Data Found' : 'There are no existing digital orders'}
              </Typography>
            ))}
        </Grid>
        <ConfirmDialog
          open={open}
          title="Confirm"
          description="Are you sure you want to delete this order?"
          confirmBtn={{
            text: 'CONFIRM',
            handler: handleConfirmBtnClick,
          }}
          cancelBtn={{
            text: 'CANCEL',
            handler: closeModal,
          }}
        />
      </>
    );
  },
);
